import React, { FC, useEffect, useRef, useState } from "react"

import { isClientSide, convertStringToKebabCase } from "@utils"

import "./ArticleNav.scss"

type NavItem = {
  id: string
  title: string
}

type ArticleNavProps = {
  className?: string
}

const ArticleNav: FC<ArticleNavProps> = ({ className }) => {
  const [navItems, setNavItems] = useState<NavItem[]>([])
  const [currentNavItem, setCurrentNavItem] = useState<string | null>(null)

  const navTitleEls = useRef<HTMLHeadingElement[] | null>(null)

  useEffect(() => {
    if (!isClientSide()) return

    navTitleEls.current =
      navTitleEls.current ||
      Array.from(
        document.querySelector(".Content.main")?.getElementsByTagName("h2") || []
      )

    if (!navTitleEls.current || navTitleEls.current.length === 0) return

    const _navItems = navTitleEls.current.map(
      (navTitleEl: HTMLHeadingElement) => {
        const navItemId =
          navTitleEl.id ||
          (navTitleEl.innerText.length > 1 &&
            convertStringToKebabCase(navTitleEl.innerText))

        return {
          id: navItemId,
          title:
            (navTitleEl.parentNode as HTMLElement)?.dataset.articleNavTitle ||
            navTitleEl.innerText,
        }
      }
    )

    setNavItems(_navItems as NavItem[])

    // If the page loads with an article already in the URL hash, set the current navItem
    const currentHash = window.location.href.includes("#")
      ? window.location.href.split("#")[1]
      : null

    setCurrentNavItem(currentHash)
  }, [])

  const handleClickLink = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    e.preventDefault()
    const titleId = e.currentTarget.href.split("#")[1]
    const titleEl = document.getElementById(titleId)
    if (!titleEl) return
    const titleTop = titleEl.getBoundingClientRect().top
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop
    window.scrollTo({
      top: Math.floor(titleTop + scrollTop - 100),
      behavior: "smooth",
    })
    window.history.pushState(null, "", `#${titleId}`)
    setCurrentNavItem(titleId)
  }

  if (!navItems || navItems.length <= 1 || navItems.length > 12) return null

  return (
    <div className={`ArticleNav ${className || ""}`}>
      <ul className="ArticleNav--list">
        {navItems.map((navItem) => (
          <li key={navItem.id}>
            <a
              className={`type--text-S ${
                navItem.id === currentNavItem && "is-active"
              }`}
              href={`#${navItem.id}`}
              onClick={handleClickLink}
            >
              {navItem.title}
            </a>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default ArticleNav
